import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    HANGING_PROTOCOLS,
    HangingProtocol,
    protocolOfTemplate,
} from '../binding/hanging';
import LayoutGlyph from '../binding/LayoutGlyph';
import { RowTemplate } from '../layout/types';
import ConsoleChoiceMenu from './ConsoleChoiceMenu';
import { ConsoleChoice } from './consoleChoiceItems';

/** A row's choice of shape, held by whoever owns the row. */
export interface RowLayoutChoice {
    /** The shape the row hangs now, named by an entry or not. */
    readonly shape: RowTemplate;
    readonly onSelect: (protocol: HangingProtocol) => void;
}

/** Edge length of the glyph that pictures a layout, in CSS pixels. */
const GLYPH_SIZE_PX = 18;

/**
 * Pictures the shape a row hangs, and picks another. The trigger is the
 * picture alone, drawn from the row's own shape; its name, or "Custom" once
 * the row hangs its own way, is in the trigger's accessible name and
 * tooltip. Each entry draws itself from its own template, so a picture and
 * its layout cannot disagree. Picking the shape the row already hangs
 * changes nothing.
 */
export default function RowLayoutMenu({
    shape,
    onSelect,
    disabled = false,
}: RowLayoutChoice & { readonly disabled?: boolean }) {
    const intl = useIntl();
    const current = protocolOfTemplate(shape);
    // Each entry is named in the user's language and pictured by its own
    // template.
    const choices = useMemo(
        (): ReadonlyArray<ConsoleChoice<HangingProtocol>> =>
            HANGING_PROTOCOLS.map((protocol) => ({
                value: protocol,
                label: intl.formatMessage({ id: protocol.labelId }),
                icon: (
                    <LayoutGlyph
                        template={protocol.template}
                        size={GLYPH_SIZE_PX}
                    />
                ),
            })),
        [intl]
    );
    return (
        <ConsoleChoiceMenu<HangingProtocol | undefined>
            name={intl.formatMessage({ id: 'rowLayout.name' })}
            testId="row-layout-trigger"
            appearance="swatch"
            // The trigger sits at the bar's right end, so the list grows
            // leftward from it and lines up with it.
            menuAlign="right"
            glyph={<LayoutGlyph template={shape} size={GLYPH_SIZE_PX} />}
            choices={choices}
            value={current}
            // Every listed choice holds a protocol; only the value in force
            // can be none.
            onSelect={(protocol) => {
                if (protocol) {
                    onSelect(protocol);
                }
            }}
            fallbackLabel={intl.formatMessage({ id: 'picker.custom' })}
            disabled={disabled}
        />
    );
}
